import { useState, useEffect } from "react";
import {
	apiGet,
	apiUpload,
	apiDelete,
	RequestConfig,
} from "../../services/api.service";
import { showSuccess, showError } from "../../services/notification.service";

interface OccupationProofInfo {
	proof_id: number;
	extra_id: number;
	file_name: string;
	mime_type: string | null;
	file_size: number;
	uploaded_at: string;
	status?: 'pending' | 'approved' | 'rejected';
	approved_at?: string | null;
	approved_by?: number | null;
	rejected_at?: string | null;
	rejected_by?: number | null;
	rejection_reason?: string | null;
}

export function useOccupationProof(extraId: number | undefined) {
	const [proofInfo, setProofInfo] = useState<OccupationProofInfo | null>(null);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);

	const fetchProofInfo = async () => {
		if (!extraId) return;

		setLoading(true);
		try {
			const config: RequestConfig = {
				notification: { showError: false },
			};

			const response = await apiGet(
				`/extras/${extraId}/occupation-proof/info`,
				undefined,
				config
			);

			// Handle the new API behavior:
			// - Success with data: extra exists and has proof
			// - Success with null data: extra exists but has no proof
			// - 404 error: extra doesn't exist
			if (response.success && response.data) {
				setProofInfo(response.data);
			} else if (response.success && response.data === null) {
				// Extra exists but has no proof
				setProofInfo(null);
			} else {
				setProofInfo(null);
			}
		} catch (error: any) {
			if (error.response?.status === 404) {
				// Extra doesn't exist - this is now an actual error
				console.error("Extra not found:", error);
			} else {
				console.error("Error fetching proof info:", error);
			}
			setProofInfo(null);
		} finally {
			setLoading(false);
		}
	};

	const uploadProof = async (file: File): Promise<boolean> => {
		if (!extraId) {
			return false;
		}

		setUploading(true);
		const formData = new FormData();
		formData.append("occupationProof", file);

		try {
			const config: RequestConfig = {
				notification: {
					showError: true,
					showSuccess: true,
				},
			};

			const response = await apiUpload(
				`/extras/${extraId}/occupation-proof`,
				formData,
				config
			);

			if (response.success && response.data) {
				setProofInfo(response.data);
				// Refetch to ensure we have the latest data
				await fetchProofInfo();
				return true;
			}

			return false;
		} catch (error: any) {
			console.error("Error uploading occupation proof:", error);
			return false;
		} finally {
			setUploading(false);
		}
	};

	const downloadProof = async () => {
		if (!extraId || !proofInfo) {
			return;
		}

		try {
			// Use fetch directly to handle blob response
			const response = await fetch(
				`${import.meta.env.VITE_API_URL || "http://localhost:3001/api"}/extras/${extraId}/occupation-proof`,
				{
					method: "GET",
					headers: {
						Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
					},
				}
			);

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(
					errorData.message ||
						errorData.error ||
						"Fehler beim Herunterladen der Datei"
				);
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", proofInfo.file_name);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			// Download was successful - we can show a generic success message here since it's a client action
			showSuccess("Datei erfolgreich heruntergeladen");
		} catch (error: any) {
			console.error("Error downloading proof:", error);
			showError(error.message || "Download fehlgeschlagen");
		}
	};

	const deleteProof = async (): Promise<boolean> => {
		if (!extraId) {
			return false;
		}

		try {
			const config: RequestConfig = {
				notification: {
					showError: true,
					showSuccess: true,
				},
			};

			const response = await apiDelete(
				`/extras/${extraId}/occupation-proof`,
				config
			);

			if (response.success) {
				setProofInfo(null);
				return true;
			}

			return false;
		} catch (error) {
			console.error("Error deleting proof:", error);
			return false;
		}
	};

	useEffect(() => {
		fetchProofInfo();
	}, [extraId]);

	return {
		proofInfo,
		loading,
		uploading,
		uploadProof,
		downloadProof,
		deleteProof,
		refetch: fetchProofInfo,
	};
}
